import React, { useState, useCallback, useMemo, useEffect } from 'react';
import HealthChips from '../Health/HealthChips';
import ScreenRow from '../List/ScreenRow';
import { resolveRow } from '../../screenRowModel';
import { useScreenHealthEntry } from '../Health/HealthContext';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from "moment";

import DeviceLastLoginListener from '../../../Services/DeviceLastLoginListener';
import FirebaseDeviceListener from '../../../Services/FirebaseDeviceListener';
import WorkingTimer from './WorkingTimer';

import { setPaused, setSelectedDevice, } from '../../../../redux/Slices/deviceSlice';
import { useGetCampaign } from '../../../../queries/campaigns';
import {
    setStepsBack,
    setIsPrayerCampaign,
    toggleDeleteDialog,
    toggleDialog
} from '../../../../redux/Slices/Global/globalSlice';
import {
    useGetDeviceInfMutation,
    useFlushDevice,
    useRestartDevice,
    useNotificationDeviceOff,
    useSendActionDevice,
    useMuteUnmuteDevice,
    useUpdateAiAnaylticDevice,
    useReissueDeviceToken
} from '../../../../queries/devices';
import DownloadingWaitingArea from './DownloadingWaitingArea';
import DevicePreview from '../Preview/DevicePreview';
import StopConfirmation from '../Setup/StopConfirmation';
import { useSkipCampaignSchedule } from '../../../../queries/campaignSchedules';
import WarnActionDialog from './WarnActionDialog';
import { SplitButtonCustom } from '../../../SharedComponents/SplitBtn/SplitButtonCustom';
import { resolveDeviceOnline, resolveDeviceStatus, resolvePlayback, resolvePlaybackDetail, resolveOptimisticPlayback, resolveScreenState } from '../../../../utils';
import { resolveMuted } from '../Health/model';
import { formatWallClock12h, arabicDeltaPhrase } from '../../../../utils/timezone';
import { formatRelativeArabic } from '../../../../utils/datetime';
import { IcAudioMuted } from '../Health/Icons';
import SchedulePausedChip from './SchedulePausedChip';

// Icon set — exact paths from .claude/pp-screens-prototype.html's .crow/
// .more/.meta/.cfoot svgs, self-contained (explicit size+stroke) so they
// render correctly wherever they're placed.
const IcMore = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={2} width={18} height={18} {...p}><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>;
const IcStop = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.9} width={16} height={16} {...p}><circle cx="12" cy="12" r="9" /><rect x="9" y="9" width="6" height="6" rx="1" /></svg>;
const IcPlay = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.9} width={16} height={16} {...p}><path d="M7 5l12 7-12 7z" /></svg>;
const IcPause = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.9} width={16} height={16} {...p}><rect x="7" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>;
const IcRestart = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.9} width={16} height={16} {...p}><path d="M21 12a9 9 0 1 1-3-6.7M21 4v5h-5" /></svg>;
const IcOpen = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={2} width={14} height={14} {...p}><path d="M15 6l-6 6 6 6" /></svg>;
// دفع محتوى — the card's new primary action (exact prototype path).
const IcPush = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={2} width={16} height={16} {...p}><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 14V8M9.5 10.5 12 8l2.5 2.5" /></svg>;
const IcPlayCircle = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.8} width={15} height={15} {...p}><circle cx="12" cy="12" r="9" /><path d="M10 8l6 4-6 4z" /></svg>;
const IcPin = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.8} width={15} height={15} {...p}><path d="M12 21s-7-5.2-7-11a7 7 0 0 1 14 0c0 5.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>;
const IcOffline = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.8} width={15} height={15} {...p}><path d="M18.4 18.4A10 10 0 0 0 5.6 5.6M1 1l22 22M8.5 16.5a5 5 0 0 1 7 0" /></svg>;
const IcClockWarn = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.8} width={15} height={15} {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
const IcStandby = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.8} width={15} height={15} {...p}><circle cx="12" cy="12" r="9" /><path d="M8 12h8" /></svg>;
const IcStandbyChip = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.9} width={13} height={13} {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v10" /></svg>;
const IcMoon = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={2} width={13} height={13} {...p}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>;
const IcAppClosed = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.8} width={15} height={15} {...p}><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8" /></svg>;
// Device-clock drift amber strip's triangle icon — exact path per spec.
const IcTriangleWarn = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={2} {...p}><path d="M12 3l9 16H3z" /><path d="M12 10v4" /><path d="M12 17h.01" /></svg>;
// content_corrupted app-chip icons — exact paths per spec. The spin
// animation itself is CSS (.pp-scr-appchip.content_retrying svg), not a prop.
const IcRefreshSpin = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.9} width={13} height={13} {...p}><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v4h-4" /></svg>;
const IcBrokenMedia = (p) => <svg viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={1.9} width={13} height={13} {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 14l4-3 3 2" /><path d="M14 5l7 7" /></svg>;

// ⋮ menu icons — exact paths from .claude/pp-dialogs-prototype.html's
// sectioned .menu (device card), plus a few not depicted in the static
// example (channel/power/edit) built in the same line-icon style.
const IcCalendar = (p) => <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></svg>;
const IcSpeaker = (p) => <svg viewBox="0 0 24 24"><path d="M11 5 6 9H3v6h3l5 4zM16 9a4 4 0 0 1 0 6" /></svg>;
const IcSpeakerMute = (p) => <svg viewBox="0 0 24 24"><path d="M11 5 6 9H3v6h3l5 4z" /><path d="M15 9l5 6M20 9l-5 6" /></svg>;
const IcCamera = (p) => <svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="14" rx="2" /><circle cx="12" cy="13" r="3.5" /><path d="M8 6l1.5-2h5L16 6" /></svg>;
// Card-level sound toggle — exact paths from .claude/pp-card-sound-prototype.html's
// approved Option 1 (.sndbtn).
const IcSoundOn = (p) => <svg viewBox="0 0 24 24"><path d="M4 9v6h3l5 4V5L7 9H4z" /><path d="M16 8.6a5 5 0 0 1 0 6.8M18.8 6.3a8.5 8.5 0 0 1 0 11.4" /></svg>;
const IcSoundOff = (p) => <svg viewBox="0 0 24 24"><path d="M4 9v6h3l5 4V5L7 9H4z" /><path d="M22 9l-6 6M16 9l6 6" /></svg>;
const IcHealthPulse = (p) => <svg viewBox="0 0 24 24"><path d="M3 12h4l2.5-6 4 12 2.5-6H21" /></svg>;
const IcBinOutline = (p) => <svg viewBox="0 0 24 24"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" /></svg>;
const IcBinFull = (p) => <svg viewBox="0 0 24 24"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6" /></svg>;
const IcLock = (p) => <svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>;
const IcChannel = (p) => <svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M8 21h8M7 6l3-3M17 6l-3-3" /></svg>;
const IcPower = (p) => <svg viewBox="0 0 24 24"><path d="M12 3v8" /><path d="M6.3 6.3a8 8 0 1 0 11.4 0" /></svg>;
const IcEdit = (p) => <svg viewBox="0 0 24 24"><path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3z" /></svg>;

const DeviceCard = ({ normal = true, above = false, data, onOnlineTimeUpdate, variant = 'card', rowUi }) => {
    const { t } = useTranslation();
    // 🔧 DEBUG FLAG: Set to true to simulate online state for mobile view testing
    const DEBUG_MOBILE_ONLINE = false;

    const { companyDetails, openDialog } = useSelector((state) => state.global);
    const fetchingCampaign = useSelector((state) => state.global.isLoading);
    const ignoredNames = new Set(["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha", "Idle"]);
    const { rtdbConnected, selectedDevice, scheduleId } = useSelector((state) => state.device);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    // Customer health chips (Internet / Sound / Picture) from the ONE shared fetch; absent until it loads -> no chips, nothing else changes.
    const healthEntry = useScreenHealthEntry(data.id);

    const [realtimeData, setRealtimeData] = useState({
        id: "",
        name: "",
        schedule_id: "",
        tag: "",
        is_playing: "",
        screen_angle: "",
        is_muted: "",
        download_percentage: {
            campaign_id: "",
            percentage: 0
        },
        media_playing: {
            currentMediaId: "",
            start_time: ""
        },
        corner: {
            id: "",
            start_time: ""
        },
        playground: {
            id: "",
            start_time: ""
        },
        banner: {
            id: "",
            start_time: ""
        },
    });

    const [currentMediaPlaying, setCurrentMediaPlaying] = useState();
    const [currentCornerPlaying, setCurrentCornerPlaying] = useState();
    const [currentPlaygroundPlaying, setCurrentPlaygroundPlaying] = useState();
    const [currentBannerPlaying, setCurrentBannerPlaying] = useState();
    // const [currentCampaignName, setCurrentCampaignName] = useState("");
    const [campaignTag, setCampaignTag] = useState("");
    const [actionRequest, setActionRequest] = useState();

    // Build 383, Part 3 — single-source-of-truth play state. The
    // optimistic guess right after a press, shown immediately instead of
    // waiting for the next 15s poll — see `resolveOptimisticPlayback`.
    const [optimisticIsPlaying, setOptimisticIsPlaying] = useState(null);
    const [optimisticSetAt, setOptimisticSetAt] = useState(null);




    const [currentCampaignId, setCurrentCampaignId] = useState("");

    useEffect(() => {

        const incoming = realtimeData?.media_playing;
        const current = currentMediaPlaying;

        const hasMediaIdChanged =
            typeof incoming?.currentMediaId === "string" &&
            incoming?.currentMediaId !== current?.currentMediaId;

        const hasStartTimeChanged = incoming?.start_time !== current?.start_time;

        const hasPauseTimeChanged =
            moment(incoming?.pause_start_time).valueOf() !==
            moment(current?.pause_start_time).valueOf();

        if (hasPauseTimeChanged) {
            const isPaused = !!incoming?.pause_start_time;
            dispatch(setPaused(isPaused));
        }
        if (hasMediaIdChanged || hasStartTimeChanged || hasPauseTimeChanged) {
            setCurrentMediaPlaying(incoming);
        }

        if (
            typeof realtimeData?.id === "string" &&
            realtimeData.id.length > 0 &&
            realtimeData.id !== currentCampaignId
        ) {
            setCurrentCampaignId(realtimeData.id);
            // setCurrentCampaignName(realtimeData.name)
        }

    }, [realtimeData?.media_playing, realtimeData?.id]);

    useEffect(() => {
        const incoming = realtimeData?.banner;
        const current = currentBannerPlaying;

        const hasMediaIdChanged = typeof incoming?.id === "string" && incoming?.id !== current?.id;
        const hasStartTimeChanged = incoming?.start_time !== current?.start_time;

        if (hasMediaIdChanged || hasStartTimeChanged) {
            setCurrentBannerPlaying(incoming);
        }
    }, [realtimeData?.banner]);

    useEffect(() => {
        const incoming = realtimeData?.playground;
        const current = currentPlaygroundPlaying;

        const hasMediaIdChanged = typeof incoming?.id === "string" && incoming?.id !== current?.id;
        const hasStartTimeChanged = incoming?.start_time !== current?.start_time;

        if (hasMediaIdChanged || hasStartTimeChanged) {
            setCurrentPlaygroundPlaying(incoming);
        }
    }, [realtimeData?.playground]);

    useEffect(() => {
        const incoming = realtimeData?.corner;
        const current = currentCornerPlaying;

        const hasMediaIdChanged = typeof incoming?.id === "string" && incoming?.id !== current?.id;
        const hasStartTimeChanged = incoming?.start_time !== current?.start_time;

        if (hasMediaIdChanged || hasStartTimeChanged) {
            setCurrentCornerPlaying(incoming);
        }
    }, [realtimeData?.corner]);


    useEffect(() => {
        if (campaignTag != realtimeData.tag) {
            setCampaignTag(realtimeData?.tag)
        }
    }, [realtimeData?.tag]);


    // useEffect(() => {
    //     if (realtimeData?.schedule_id != scheduleId) {
    //         dispatch(setScheduleId(realtimeData?.schedule_id))
    //     }
    // }, [realtimeData?.schedule_id]);

    const { isLoading, data: campaignInfo, refetch } = useGetCampaign({
        enabled: false,
        id: realtimeData?.id
    });

    const turnOnOffDevice = useNotificationDeviceOff();
    const restartDevice = useRestartDevice();
    const reissueToken  = useReissueDeviceToken();
    const getDeviceInfo = useGetDeviceInfMutation();
    const flushDevice = useFlushDevice();
    const skipCampaignSchedule = useSkipCampaignSchedule();
    const deviceAction = useSendActionDevice()
    const aiAnalyticAction = useUpdateAiAnaylticDevice()
    const muteUnmuteDevice = useMuteUnmuteDevice();
    // Memoized selectors and derived values
    const isOnline = useMemo(() => DEBUG_MOBILE_ONLINE || resolveDeviceOnline(data?.connectivity, data?.online), [data?.connectivity, data?.online, DEBUG_MOBILE_ONLINE]);

    // Device-clock drift (2026-09-17): superseded the old device_info.timezone
    // vs resolved_timezone client-side check (which never actually fired on
    // this card — this endpoint, GroupController::companyGroups, never set
    // resolved_timezone) with a backend-computed one, driven by
    // device_utc_offset_minutes reported on every 10s heartbeat rather than
    // the infrequent device/update payload. See Device::timezoneMismatch().
    // 'unknown' (older player, field absent) deliberately renders nothing —
    // never a false alarm.
    const deviceLocalTimeLabel = useMemo(() => formatWallClock12h(data?.device_local_time), [data?.device_local_time]);
    const branchLocalTimeLabel = useMemo(() => formatWallClock12h(data?.branch_local_time), [data?.branch_local_time]);
    const timezoneMismatch = data?.timezone_mismatch === true;
    // The clock row and its match/drift indicator both claim something about
    // the device's CURRENT state — showing them for an offline/scheduled_off
    // device would display a stale reading as if it were live, and showing
    // them when resolvedTimezone() fell through to the hardcoded default
    // (Device::timezoneSource() === 'fallback' — no branch or company
    // timezone set) would claim a "match" against a value that has nothing
    // to do with the venue. `branch`/`company` (with `.timezone`) are already
    // eager-loaded and present on this payload — mirrors
    // Device::timezoneSource()'s own branch-then-company-then-fallback chain
    // client-side rather than needing a new field from the backend.
    const hasRealResolvedTimezone = Boolean(data?.branch?.timezone || data?.company?.timezone);
    const showDeviceClockInfo = isOnline && hasRealResolvedTimezone;
    // build 396: ONE mute truth for the speaker button AND the Sound chip -- the live RTDB isMuted (as before) with the backend's as the fallback.
    const isMutedNow = resolveMuted(realtimeData.is_muted, healthEntry?.muted) === true;
    const deviceStatus = useMemo(() => DEBUG_MOBILE_ONLINE ? 'online' : resolveDeviceStatus(data?.connectivity, data?.presence_status, data?.online), [data?.connectivity, data?.presence_status, data?.online, DEBUG_MOBILE_ONLINE]);
    // Build 383, Part 3 — single-source-of-truth play state. Resolved from
    // the backend's `connectivity.playback` (same 15s `/groups/company`
    // poll `deviceStatus` above already reads), through any optimistic
    // guess a recent press is still waiting to see confirmed. Replaces
    // every `realtimeData.is_playing` read below — RTDB `DeviceState` is a
    // compatibility mirror for old remotes only now (see
    // popuppush-app-v2's CLAUDE.md), never read here for display.
    const isPlaying = useMemo(() => {
        const serverIsPlaying = resolvePlayback(data?.connectivity);
        const decision = resolveOptimisticPlayback({
            serverIsPlaying, optimisticIsPlaying, optimisticSetAt, now: Date.now(),
        });
        return decision.displayIsPlaying;
    }, [data?.connectivity, optimisticIsPlaying, optimisticSetAt]);
    useEffect(() => {
        const serverIsPlaying = resolvePlayback(data?.connectivity);
        const decision = resolveOptimisticPlayback({
            serverIsPlaying, optimisticIsPlaying, optimisticSetAt, now: Date.now(),
        });
        if (!decision.stillOptimistic && optimisticIsPlaying !== null) {
            setOptimisticIsPlaying(null);
            setOptimisticSetAt(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.connectivity]);
    // Display-text-only fallback for the "now playing" label below — trust RTDB's
    // realtimeData.name while its WebSocket is actually connected (unchanged
    // behavior); once it's not, realtimeData.name can be a populated-but-stale
    // campaign name (device switched via the REST fallback and can't write RTDB),
    // so prefer the REST-computed current_campaign_name instead. Deliberately does
    // NOT touch the Downloading.../Updating.../Prayer-break sentinel checks or the
    // click-handler gating elsewhere in this file — those read realtimeData.name
    // directly, since they're about last-known operational state, not display text.
    const displayCampaignName = useMemo(() => {
        const isRtdbLive = rtdbConnected?.[data?.serial_name] === true;
        // RTDB connected (authoritative), or no REST value to fall back to.
        if (isRtdbLive || !data?.current_campaign_name) {
            return realtimeData.name;
        }
        // RTDB disconnected — realtimeData.name may be a stale pre-disconnect
        // value, so trust the REST-computed name instead.
        return data.current_campaign_name;
    }, [rtdbConnected, data?.serial_name, data?.current_campaign_name, realtimeData.name]);
    const supportsSatellite = useMemo(() => data?.device_info?.satellite_is_available !== 0, [data?.device_info]);
    const hasScheduleFeature = useMemo(() => companyDetails?.package?.features?.advance_content_schedule, [companyDetails]);
    const hasPreviewFeature = useMemo(() => companyDetails?.package?.features?.device_preview, [companyDetails]);

    const hasSatelliteFeature = useMemo(() => companyDetails?.package?.features?.media_type?.includes("satellite"), [companyDetails]);
    useEffect(() => {
        if (!onOnlineTimeUpdate) return;

        let interval;
        if (isOnline && realtimeData?.first_login) {
            interval = setInterval(() => {
                const secondsOnline = moment().diff(moment(realtimeData.first_login), "seconds");
                onOnlineTimeUpdate(data.id, secondsOnline); // 🔧 report up
            }, 10000); // every 10 seconds
        } else {
            onOnlineTimeUpdate(data.id, 0); // 🔧 report zero when offline
        }

        return () => clearInterval(interval);
    }, [isOnline, realtimeData?.first_login]);
    // Handler functions with useCallback
    const confirmDeleteDevice = useCallback(() => {
        dispatch(setSelectedDevice(data));
        dispatch(toggleDeleteDialog("device"));
    }, [dispatch, data]);

    const openConfirmDialog = useCallback((action) => {
        setActionRequest(action)
        dispatch(setSelectedDevice(data));

        dispatch(toggleDialog("warnActionDialog"));

    }, [dispatch, data]);

    const confirmAction = useCallback(async () => {
        if (actionRequest === "restart") {
            restartHandler()
        } else if (actionRequest === "turn_off") {
            turnOnTurnOff(data.fcm_token, "turn_off")
        } else if (actionRequest === "flush") {
            turnOnTurnOff(data.fcm_token, "flush")
        } else if (actionRequest === "unmute" || actionRequest === "mute") {
            muteUnMute(data.id, realtimeData.is_muted ? "unmute" : "mute")
        }
        else if (actionRequest === "pause" || actionRequest === "play") {
            // Build 383, Part 3 — `actionRequest` already IS the exact
            // action string the confirm dialog showed the user (set by
            // openConfirmDialog at click time) — sending it directly,
            // instead of re-deriving "pause"/"play" from is_playing again
            // here, means a value that changed between opening the dialog
            // and confirming it can never send the OPPOSITE of what the
            // dialog said it would do. Then show the predicted new state
            // immediately (optimistic) rather than waiting for the next
            // 15s poll — see `resolveOptimisticPlayback`.
            playPause(data.id, actionRequest)
            setOptimisticIsPlaying(actionRequest === "play")
            setOptimisticSetAt(Date.now())
        }
        else if (actionRequest === "ai_analytics") {
            switchAiAnalytics(data.id, data.ai_analytics ? false : true)
        }
        else if (actionRequest === "reissue_token") {
            await reissueToken.mutateAsync({ id: data.id });
            dispatch(toggleDialog("warnActionDialog"));
            setActionRequest();
        }
    }, [actionRequest, data, realtimeData.is_muted, reissueToken, dispatch])

    const openEditDialog = useCallback(() => {
        dispatch(setSelectedDevice(data));
        dispatch(toggleDialog("updateDevice"));
    }, [dispatch, data]);

    const devicePreview = useCallback(() => {
        dispatch(setSelectedDevice(data));
        const isPaused = !!realtimeData?.media_playing?.pause_start_time && !isPlaying;
        dispatch(setPaused(isPaused));
        dispatch(toggleDialog("devicePreview"));
    }, [isPlaying, realtimeData.media_playing]);

    const stopScheduledCampaign = useCallback(async () => {
        if (realtimeData.schedule_id) {
            const schedule_id = realtimeData.schedule_id
            // Stops just this running occurrence and reverts to whatever was
            // playing before the schedule started; the recurring schedule itself
            // stays intact for next time (same op as "Skip This Occurrence").
            await skipCampaignSchedule.mutateAsync({ id: schedule_id })
            dispatch(toggleDialog("stopConfirmation"));
        }
    }, [realtimeData.schedule_id])

    const openAssignDialog = useCallback(async () => {
        dispatch(toggleDialog("assignCampaignsDropDown"));

        dispatch(setSelectedDevice(data));
        await getDeviceInfo.mutateAsync({ id: data.id });
    }, [dispatch, getDeviceInfo, data]);

    // Shortcuts straight to the new calendar with this device pre-selected —
    // replaces the old (broken) scheduling popup.
    const openAssignScheduleCampaignDialog = useCallback(() => {
        navigate(`/schedule/themes?device=${data.id}`);
    }, [navigate, data.id]);


    const playPause = useCallback(async (deviceID, action) => {
        await deviceAction.mutateAsync({
            id: deviceID,
            data: { action: action.toLowerCase() }
        }, {
            onSuccess: () => {
                dispatch(toggleDialog("warnActionDialog"));
                setActionRequest()
            }
        });
    }, [deviceAction]);

    const muteUnMute = useCallback(async (deviceID, action) => {
        await muteUnmuteDevice.mutateAsync({
            id: deviceID,
            data: { action: action.toLowerCase() }
        }, {
            onSuccess: () => {
                dispatch(toggleDialog("warnActionDialog"));
                setActionRequest()
            }
        });
    }, [muteUnmuteDevice]);


    // Select this device and open the screenshot dialog. The dialog
    // (ScreenshotPreviewer) captures a fresh screenshot itself — sends the
    // action + polls Storage for the new upload — so we don't send it here
    // (doing so before opening showed the stale image / "nothing happened").
    const screenShot = useCallback(() => {
        dispatch(setSelectedDevice(data));
        dispatch(toggleDialog("screenshot"));
    }, [dispatch, data]);

    const turnOnTurnOff = useCallback(async (token, action) => {

        await turnOnOffDevice.mutateAsync({
            token: token,
            data: { action: action }
        }, {
            onSuccess: () => {
                if (actionRequest === "flush" || actionRequest === "turn_off") {
                    dispatch(toggleDialog("warnActionDialog"));
                    setActionRequest()
                }
            }
        });




    }, [turnOnOffDevice]);

    // Single "launch/start the app" entry point — used by BOTH the card's
    // app-closed quick button and the maintenance menu's Start item, so
    // there is exactly one code path, not two that happen to look alike.
    // One-shot turn_on only — an earlier multi-retry version chased a
    // flaky-launch symptom that turned out to correlate with the device's
    // own process instability, not a fixable client-side gap, and hammering
    // an unstable device with repeated turn_ons risked making that worse.
    const startAppOnDevice = useCallback(() => {
        turnOnTurnOff(data.fcm_token, "turn_on");
    }, [turnOnTurnOff, data.fcm_token]);

    const goToWaiting = useCallback(() => {

        if (realtimeData.download_percentage && realtimeData.download_percentage.percentage < 100) {
            dispatch(setSelectedDevice(data))
            dispatch(toggleDialog("downloadProgress"));
        }

    }, [realtimeData.download_percentage])

    const goToCampaign = useCallback(() => {
        if (isOnline) {
            dispatch(setStepsBack(-1));
            refetch();
            if (!isLoading) {
                dispatch(setIsPrayerCampaign(false));
                sessionStorage.setItem('is_default_prayer', JSON.stringify(false));

                if (campaignInfo?.is_interactive) {
                    navigate(`/interactive/campaign/${realtimeData?.id}/setup`);
                } else {
                    navigate(`/campaigns/${realtimeData?.id}/setup`);
                }
            }
        }
    }, [isOnline, isLoading, campaignInfo, realtimeData?.id]);

    const channelHandler = useCallback(() => {
        const selected_device = {
            id: data?.id,
            serial_name: data.serial_name,
            online: isOnline,
            name: data.name
        };
        dispatch(setSelectedDevice(selected_device));
        dispatch(toggleDialog("createCampaign"));
        navigate("/campaigns/list");
    }, [data, isOnline]);

    // const flushHandler = useCallback(async () => {
    //     await flushDevice.mutateAsync({ id: data?.id });
    // }, []);

    const restartHandler = useCallback(async () => {
        await restartDevice.mutateAsync({ id: data?.id }, {
            onSuccess: () => {
                // if (actionRequest === "restart") {
                dispatch(toggleDialog("warnActionDialog"));
                setActionRequest()
                // }
            }
        });
    }, [data?.id]);

    const goToInfo = useCallback(() => {
        // dispatch(setSelectedDevice(data));
        navigate(`/devices/${data.id}/show`);
    }, [data]);


    const openStopDialog = useCallback(() => {
        dispatch(setSelectedDevice(data));
        dispatch(toggleDialog("stopConfirmation"))
    }, [dispatch, data]);

    const switchAiAnalytics = useCallback(async (deviceID, action) => {
        await aiAnalyticAction.mutateAsync({ id: deviceID, data: { ai_analytic: action } }, {
            onSuccess: () => {
                dispatch(toggleDialog("warnActionDialog"));
                setActionRequest()
            }
        });
    }, [data]);


    // Memoized items for SplitButton — sectioned per
    // .claude/pp-dialogs-prototype.html's device ⋮ menu (المحتوى / التشغيل /
    // الصيانة + a separated red حذف). Two items dropped entirely because the
    // prototype's own annotations call them out as redundant, verified safe
    // here (both destinations stay reachable elsewhere on the card):
    //  - "دفع محتوى" (assign campaign) — now only the card's primary IcPush
    //    button (openAssignDialog is also wired there).
    //  - "معلومات الجهاز" (device info) — duplicate of "فتح"/the device name,
    //    both of which already call goToInfo.
    //  - "استلام الشاشة الآن" (take-over) dropped too: identical handler to
    //    "تعيين جدول" (both just navigate to the calendar), not its own
    //    feature.
    // AI Analytics is hidden (visible:false), not removed, per explicit
    // instruction — command/mutation/handler all stay intact for when it's
    // surfaced again.
    const items = useMemo(() => [
        { type: 'label', text: t('devices.menu_section_content') },
        {
            label: <span className="fs-6">{t('buttons.edit')}</span>,
            icon: <IcEdit />,
            command: openEditDialog
        },
        {
            // Shortcut to the calendar-based Theme Schedule (CafeThemeSchedule.jsx)
            // with this device pre-selected — no longer opens the old popup.
            label: <span className="fs-6">{t('devices.group_assign_schedule')}</span>,
            icon: <IcCalendar />,
            command: openAssignScheduleCampaignDialog,
            visible: hasScheduleFeature
        },
        {
            label: (
                <span className='flex flex-col'>
                    {t('devices.group_assign_channel')}
                    {!supportsSatellite &&
                        <small className='text-[10px] text-[red] font-bold'>
                            {t('devices.group_no_satellite_support')}
                        </small>
                    }
                </span>
            ),
            icon: <IcChannel />,
            command: channelHandler,
            visible: hasSatelliteFeature,
            disabled: !supportsSatellite
        },

        { type: 'sep' },
        { type: 'label', text: t('devices.menu_section_playback') },
        {
            // Gated on deviceStatus === 'online' (actively playing), not the
            // reachability boolean isOnline — standby/app_closed are
            // reachable (Phase 3) but plain play/pause on either of those
            // resumes playback without clearing standby, the same bug class
            // fixed on the card's own standby Play button. Offline devices
            // never receive the action either way (no RTDB/FCM channel to
            // reach), and realtimeData here is only last-known state — so
            // keep this visible but disabled, matching the card's own
            // primary play/pause button which hides entirely when not
            // actively online.
            label: <span className="fs-6">{isPlaying ? t('buttons.pause') : t('devices.group_play')}</span>,
            icon: isPlaying ? <IcPause /> : <IcPlay />,
            command: () => openConfirmDialog(isPlaying ? "pause" : "play"),
            disabled: deviceStatus !== 'online'
        },
        {
            label: <span className="fs-6">{realtimeData.is_muted ? t('devices.group_unmute') : t('devices.group_mute')}</span>,
            icon: realtimeData.is_muted ? <IcSpeakerMute /> : <IcSpeaker />,
            command: () => openConfirmDialog(realtimeData.is_muted ? "unmute" : "mute"),
            disabled: !isOnline
        },
        {
            label: <span className="fs-6">{t('devices.group_screenshot')}</span>,
            icon: <IcCamera />,
            visible: isOnline,
            command: () => screenShot(data.id, "screenShot")
        },
        {
            label: <span className="fs-6">{t('buttons.preview')}</span>,
            icon: <IcPlayCircle />,
            command: devicePreview,
            visible: hasPreviewFeature ? isOnline && realtimeData.name != "Downloading..." && realtimeData.name != "Updating..." : false
        },
        {
            // Kept wired but hidden from this menu — do not remove the
            // handler/mutation, only the entry point.
            label: <span className="fs-6">{data.ai_analytics ? t('devices.group_turn_off_ai') : t('devices.group_turn_on_ai')}</span>,
            icon: <IcPlayCircle />,
            command: () => openConfirmDialog("ai_analytics"),
            visible: false
        },

        { type: 'sep' },
        { type: 'label', text: t('devices.menu_section_maintenance') },
        {
            // deviceStatus === 'online' (actively playing), not isOnline
            // (reachability) — a standby device IS reachable (Phase 3) but
            // still needs the toggle to offer turn_on/Start, not another
            // turn_off, to actually exit the soft lock.
            label: <span className="fs-6">{deviceStatus === 'online' ? t('devices.group_turn_off') : t('devices.group_turn_on')}</span>,
            icon: <IcPower />,
            command: () => {
                if (deviceStatus === 'online') {
                    openConfirmDialog("turn_off")
                } else {
                    startAppOnDevice()
                }
            },
            visible: !!data.fcm_token
        },
        {
            // Restart kills the running app and relaunches it from scratch —
            // only meaningful when the app is actually running. Sent against
            // an app-closed device it kills a live-but-backgrounded process
            // with nothing to relaunch, dropping presence to offline for no
            // benefit; app-closed already has its own correct action (Start,
            // below) that brings the existing process forward without
            // killing it. Confirmed live on hardware: Restart on an
            // app-closed device triggers a real process kill+relaunch cycle
            // ("Process com.ubitc.popuppush restarted" in logcat).
            label: <span className="fs-6">{t('devices.group_restart')}</span>,
            icon: <IcRestart />,
            command: () => openConfirmDialog("restart"),
            visible: deviceStatus !== 'app_closed'
        },
        {
            // Customer Screen health for THIS screen.
            label: <span className="fs-6">{t('sh.nav_this_screen')}</span>,
            icon: <IcHealthPulse />,
            command: () => navigate(`/devices/${data.id}/health`)
        },
        {
            label: <span className="fs-6">{t('devices.group_flush')}</span>,
            icon: <IcBinOutline />,
            command: () => openConfirmDialog("flush")
        },
        {
            // Support recovery — offered only when the device looks STUCK:
            // plain 'offline' (heartbeat missing, unexplained). Deliberately
            // NOT shown for 'scheduled_off' (2026-09-17) — a café closed for
            // the night per its own schedule isn't a fault, and surfacing a
            // "reissue token" troubleshooting action there would read as a
            // false alarm. A device that's really online and fetching
            // content doesn't need a new token either, and hiding the option
            // keeps the menu from looking like a one-button reset for anyone
            // who doesn't know what it does.
            label: <span className="fs-6">{t('devices.group_reissue_token')}</span>,
            icon: <IcLock />,
            visible: deviceStatus === 'offline',
            command: () => openConfirmDialog("reissue_token")
        },

        { type: 'sep' },
        {
            label: <span className="fs-6">{t('devices.group_delete_screen')}</span>,
            icon: <IcBinFull />,
            command: confirmDeleteDevice,
            danger: true
        }
    ], [realtimeData, isOnline, deviceStatus, data.ai_analytics, data.fcm_token, t, confirmDeleteDevice, startAppOnDevice]);

    // Phase 3: dot/card accent = reachability only — the 4-way content-state
    // (playing/paused/standby/app_closed) lives solely in the appchip below,
    // never the dot color. scheduled_off (2026-09-17) is a THIRD, calm
    // unreachable variant (purple, not red) — still `isOnline === false`
    // (see resolveDeviceOnline), so it's read off deviceStatus, not isOnline.
    const statusClass = isOnline ? 'on' : deviceStatus === 'scheduled_off' ? 'scheduled_off' : 'off';
    // content_corrupted (2026-09-18): reachability (dot's normal role) is
    // fine here — the box IS online, only the CONTENT is broken. So the
    // reach badge keeps its ordinary 'on' (green, "متصل") styling via
    // statusClass above, but the DOT alone gets its own amber-pulsing
    // class — the one place dot and reach badge intentionally diverge.
    const dotClass = deviceStatus === 'content_corrupted' ? 'content_corrupted' : statusClass;
    // Same threshold CampaignPlayer.MAX_CONTENT_RETRY_ATTEMPTS uses to
    // actually stop auto-retrying — keeps this card's RETRYING/EXHAUSTED
    // split in sync with when the player genuinely gives up.
    const MAX_CONTENT_RETRY_ATTEMPTS = 5;
    const contentCorruptedAttempts = data?.content_corrupted_attempts ?? 0;
    const isContentCorruptedRetrying = contentCorruptedAttempts < MAX_CONTENT_RETRY_ATTEMPTS;
    // operatingStartTime is a bare "HH:mm" wall-clock value already in the
    // device's own configured hours (not a UTC instant) — no timezone
    // conversion needed, just 12-hour formatting. Latin digits (matches the
    // presence surface's own convention, e.g. the uptime badge), literal
    // ص/م per the approved design — not locale-switched to AM/PM.
    const scheduledOffOpeningLabel = useMemo(
        () => formatWallClock12h(data?.device_settings?.operatingStartTime),
        [data?.device_settings?.operatingStartTime]
    );
    // `isPlaying === null` means "don't know yet" (e.g. a device whose
    // player build predates is_playing on the heartbeat, or before the
    // first poll resolves) — show no play/pause chip rather than
    // guessing 'pause', same tri-state reasoning as `resolvePlayback`.
    // build 396 (Part D): the pause chip comes from the SAME function the one-screen page's state is pinned to (resolveScreenState).
    const cardScreenState = deviceStatus === 'online' ? resolveScreenState(data?.connectivity, data?.presence_status, data?.online, isPlaying, healthEntry?.state) : deviceStatus;
    // build 402: the shared label rule -- "playing without sound" / "loading" are NOT "paused" (see resolvePlaybackDetail)
    const playbackDetail = resolvePlaybackDetail(data?.connectivity);
    // build 405: the player's CURRENT audio health ({state: ok|repairing|failed|external_fault}); absent on an old player (nothing changes for it)
    const audioHealthState = data?.connectivity?.audio_health?.state;
    const appChipState = cardScreenState === 'paused' ? 'pause'
        : (deviceStatus === 'online' && (playbackDetail === 'external_fault' || audioHealthState === 'external_fault')) ? 'external_fault'
        : (deviceStatus === 'online' && audioHealthState === 'repairing') ? 'audio_repairing'
        : (deviceStatus === 'online' && audioHealthState === 'failed') ? 'audio_failed'
        : (deviceStatus === 'online' && playbackDetail === 'no_sound') ? 'no_sound'
        : (deviceStatus === 'online' && playbackDetail === 'loading') ? 'loading'
        : (deviceStatus === 'online' && playbackDetail === 'media_stalled') ? 'media_stalled'
        : (deviceStatus === 'online' && playbackDetail === 'prayer') ? 'prayer'
        : deviceStatus === 'online'
        ? (isPlaying === null ? null : 'play')
        : deviceStatus === 'standby' ? 'standby'
            : deviceStatus === 'app_closed' ? 'closed'
                : deviceStatus === 'content_corrupted'
                    ? (isContentCorruptedRetrying ? 'content_retrying' : 'content_exhausted')
                    : null;
    const nowPlayingClickHandler = (realtimeData.name === "Prayer break" || realtimeData.name === "Updating...")
        ? undefined
        : realtimeData.name === "Downloading..." ? goToWaiting : goToCampaign;
    let nowPlayingIcon = "fas fa-bolt";
    let nowPlayingLabel = displayCampaignName || "---";
    if (realtimeData.name === "Downloading...") {
        nowPlayingLabel = "Downloading...";
    } else if (realtimeData.name === "Updating...") {
        nowPlayingLabel = "Updating...";
    } else if (realtimeData.tag === "scheduled") {
        nowPlayingIcon = "fas fa-calendar-days";
        nowPlayingLabel = displayCampaignName || "---";
    } else if (realtimeData.tag === "prayer" || realtimeData.name === "Prayer break") {
        nowPlayingIcon = "fas fa-mosque";
        nowPlayingLabel = displayCampaignName || "---";
    }
    // "brand zone/city" for the drift strip — the branch name already shown
    // lower on the card (IcPin row) is more legible than the raw IANA id;
    // fall back to the id itself for a device on no branch (company-level
    // fallback).
    const branchZoneLabel = data?.branch?.name || data?.resolved_timezone || '';
    const driftDirection = (data?.offset_delta_minutes ?? 0) > 0
        ? t('devices.tz_drift_ahead')
        : t('devices.tz_drift_behind');
    const driftAmount = arabicDeltaPhrase(Math.abs(data?.offset_delta_minutes ?? 0));

    // The dialogs and realtime listeners every screen needs, whichever way it is drawn (card or list row).
    const sharedTail = (
        <>
            {openDialog.downloadProgress
                && selectedDevice?.id === data.id &&
                typeof realtimeData.download_percentage?.campaign_id === 'string' &&
                realtimeData.download_percentage?.campaign_id.length > 0 && (
                    <DownloadingWaitingArea realtimeData={realtimeData} />
                )}

            {openDialog.devicePreview && selectedDevice?.id === data.id && (<DevicePreview
                campaignTag={campaignTag}
                currentMediaPlaying={currentMediaPlaying}
                currentPlaygroundPlaying={currentPlaygroundPlaying}
                currentBannerPlaying={currentBannerPlaying}
                currentCornerPlaying={currentCornerPlaying} 
                currentCampaignId={currentCampaignId} 
                name={realtimeData.name}
                degree={realtimeData.screen_angle || data?.rotation || 0}
            />)}


            {openDialog.stopConfirmation && selectedDevice?.id === data.id && (<StopConfirmation
                stopHandler={stopScheduledCampaign} isLoading={skipCampaignSchedule.isLoading} />)}


            {openDialog.warnActionDialog && selectedDevice?.id === data.id && actionRequest && (<WarnActionDialog action={actionRequest} handleConfirm={confirmAction} isLoading={turnOnOffDevice.isLoading || restartDevice.isLoading} />)}

            <FirebaseDeviceListener
                setRealtimeData={setRealtimeData}
                path={`Devices/${data.serial_name}/DeviceState`}
            />

            <DeviceLastLoginListener
                serialName={data.serial_name}
                path={`Devices/${data.serial_name}/presence`}
                apiOnline={data.online}
                apiPresenceStatus={data.presence_status}
            />
        </>
    );

    // LIST view (screens-list-prototype): the SAME hooks/state/dialogs as the card, only the markup differs -- the row can never disagree with the card.
    if (variant === 'row') {
        const row = resolveRow({ data, healthEntry, isPlaying, rtdbMuted: realtimeData.is_muted });
        return (
            <>
                <ScreenRow
                    data={data}
                    row={row}
                    nowLabel={deviceStatus === 'online' && nowPlayingLabel !== '---' ? nowPlayingLabel : ''}
                    firstLogin={realtimeData.first_login}
                    expanded={!!rowUi?.expanded}
                    onToggle={rowUi?.onToggle}
                    onReport={rowUi?.onReport}
                    handleProps={rowUi?.handleProps}
                    dragging={!!rowUi?.dragging}
                    pushTitle={isOnline ? t('devices.action_push_content') : t('devices.action_push_content_offline')}
                    onPush={fetchingCampaign ? () => {} : openAssignDialog}
                    onTogglePlay={() => openConfirmDialog(isPlaying ? "pause" : "play")}
                    onOpen={goToInfo}
                />
                {sharedTail}
            </>
        );
    }

    return (
        <>
            <div className={`pp-scr-card ${statusClass}`}>
                <div className="pp-scr-crow">
                    <span className={`pp-scr-dot ${dotClass}`}></span>
                    <button type="button" className="pp-scr-dname" onClick={goToInfo}>{data.name || "---"}</button>
                    {realtimeData.schedule_id && deviceStatus === 'online' && (
                        <button type="button" className="pp-scr-stop" onClick={openStopDialog}
                            title={t('devices.group_stop_scheduled_tooltip')} aria-label={t('devices.group_stop_scheduled_tooltip')}>
                            <IcStop />
                        </button>
                    )}
                    <SplitButtonCustom items={items} className="pp-scr-more" icon={<IcMore />} />
                </div>

                {/* Phase 3: reachability (dot color echoed here) is now
                    fully separate from app content-state — a standby/
                    app_closed device is still "متصل" (reach.on), with its
                    content-state as its own chip alongside, never folded
                    into a single 4-state badge. */}
                <div className="pp-scr-status">
                    <span className={`pp-scr-reach ${statusClass}`}>
                        {statusClass === 'scheduled_off' && <IcMoon />}
                        <span>{isOnline ? t('status.online') : deviceStatus === 'scheduled_off' ? t('status.scheduled_off') : t('status.offline')}</span>
                        {appChipState === 'play' && <> · <WorkingTimer time={realtimeData.first_login} small /></>}
                    </span>
                    {appChipState === 'play' && (
                        <span className="pp-scr-appchip play"><IcPlayCircle />{t('devices.appstate_playing')}</span>
                    )}
                    {appChipState === 'pause' && (
                        <span className="pp-scr-appchip pause"><IcPause />{t('devices.appstate_paused')}</span>
                    )}
                    {appChipState === 'no_sound' && (
                        <span className="pp-scr-appchip no_sound" data-testid="appchip-no-sound"><IcAudioMuted />{t('devices.appstate_no_sound')}</span>
                    )}
                    {appChipState === 'external_fault' && (
                        <span className="pp-scr-appchip external_fault" data-testid="appchip-external-fault"><IcAudioMuted />{t('devices.appstate_external_fault')}</span>
                    )}
                    {appChipState === 'audio_repairing' && (
                        <span className="pp-scr-appchip audio_repairing" data-testid="appchip-audio-repairing"><IcRefreshSpin />{t('devices.appstate_audio_repairing')}</span>
                    )}
                    {appChipState === 'audio_failed' && (
                        <span className="pp-scr-appchip audio_failed" data-testid="appchip-audio-failed"><IcAudioMuted />{t('devices.appstate_audio_failed')}</span>
                    )}
                    {appChipState === 'media_stalled' && (
                        <span className="pp-scr-appchip media_stalled" data-testid="appchip-media-stalled"><IcBrokenMedia />{t('devices.appstate_media_stalled')}</span>
                    )}
                    {appChipState === 'prayer' && (
                        <span className="pp-scr-appchip prayer" data-testid="appchip-prayer"><IcMoon />{t('devices.appstate_prayer')}</span>
                    )}
                    {appChipState === 'loading' && (
                        <span className="pp-scr-appchip loading" data-testid="appchip-loading"><IcRefreshSpin />{t('devices.appstate_loading')}</span>
                    )}
                    {appChipState === 'standby' && (
                        <span className="pp-scr-appchip standby"><IcStandbyChip />{t('status.standby')}</span>
                    )}
                    {appChipState === 'closed' && (
                        <span className="pp-scr-appchip closed"><IcAppClosed />{t('status.app_closed')}</span>
                    )}
                    {appChipState === 'content_retrying' && (
                        <span className="pp-scr-appchip content_retrying"><IcRefreshSpin />{t('devices.content_retrying_chip')}</span>
                    )}
                    {appChipState === 'content_exhausted' && (
                        <span className="pp-scr-appchip content_exhausted"><IcBrokenMedia />{t('devices.content_exhausted_chip')}</span>
                    )}
                    {/* schedule v4: a person changed this screen by hand during a schedule window; the schedule is paused until `until_local` */}
                    <SchedulePausedChip paused={data?.schedule_paused} deviceId={data?.id} tz={data?.resolved_timezone} />
                </div>

                <HealthChips comps={healthEntry?.components} muted={resolveMuted(realtimeData.is_muted, healthEntry?.muted)} />

                <div className="pp-scr-meta">
                    {deviceStatus === 'online' && (
                        <div className="m play" onClick={nowPlayingClickHandler}>
                            <IcPlayCircle />
                            <span className="v"><i className={`${nowPlayingIcon} me-1`}></i>{nowPlayingLabel}</span>
                            <button type="button"
                                className={`pp-scr-sndbtn ${isMutedNow ? 'muted' : 'on'}`}
                                data-action={isMutedNow ? 'unmute' : 'mute'}
                                title={isMutedNow ? t('devices.sound_muted_tooltip') : t('devices.sound_on_tooltip')}
                                aria-label={isMutedNow ? t('sh.btn_unmute') : t('sh.btn_mute')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    muteUnmuteDevice.mutateAsync({ id: data.id, data: { action: isMutedNow ? "unmute" : "mute" } });
                                }}>
                                {/* build 396: the button NAMES the action it performs; the speaker glyph is never the only signal */}
                                {isMutedNow ? <IcSoundOff /> : <IcSoundOn />}<span className="pp-scr-sndlbl">{isMutedNow ? t('sh.btn_unmute') : t('sh.btn_mute')}</span>
                            </button>
                        </div>
                    )}
                    {deviceStatus === 'standby' && (
                        <div className="m muted-row"><IcStandby /><span className="v">{t('devices.now_playing_none')}</span></div>
                    )}
                    {deviceStatus === 'app_closed' && (
                        <div className="m muted-row"><IcAppClosed /><span className="v">{t('devices.app_closed_meta')}</span></div>
                    )}
                    {deviceStatus === 'offline' && (
                        <>
                            <div className="m muted-row"><IcOffline /><span className="v">{t('devices.last_seen', { time: formatRelativeArabic(data?.connectivity?.last_seen_at || data?.last_login || data?.created_at) })}</span></div>
                            <div className="m warn-row"><span className="v">{t('devices.offline_onsite_hint')}</span></div>
                        </>
                    )}
                    {deviceStatus === 'scheduled_off' && (
                        <div className="m muted-row">
                            <IcClockWarn />
                            <span className="v tnum">
                                {scheduledOffOpeningLabel
                                    ? t('devices.scheduled_off_meta', { opening: scheduledOffOpeningLabel })
                                    : t('status.scheduled_off')}
                            </span>
                        </div>
                    )}
                    {/* Device-clock drift: shown only for a device that's
                        actually live-reporting right now (online/standby/
                        app_closed/content_corrupted — not offline, not
                        scheduled_off, see resolveDeviceOnline) AND has a
                        REAL resolved timezone (branch or company, not the
                        hardcoded fallback) — see showDeviceClockInfo above.
                        Absent report (older player, or gated off) renders
                        nothing, never a stale reading or a "match" against
                        a meaningless default. */}
                    {showDeviceClockInfo && deviceLocalTimeLabel && (
                        <div className="m muted-row">
                            <IcClockWarn />
                            <span className="v tnum">
                                {timezoneMismatch
                                    ? t('devices.device_time_mismatch', { time: deviceLocalTimeLabel, branchTime: branchLocalTimeLabel })
                                    : t('devices.device_time_matched', { time: deviceLocalTimeLabel })}
                            </span>
                        </div>
                    )}
                    {deviceStatus === 'content_corrupted' && isContentCorruptedRetrying && (
                        <div className="m muted-row">
                            <IcClockWarn />
                            <span className="v tnum">
                                {t('devices.content_retrying_meta', {
                                    n: contentCorruptedAttempts,
                                    time: deviceLocalTimeLabel || branchLocalTimeLabel || ''
                                })}
                            </span>
                        </div>
                    )}
                    {data?.branch?.name && (
                        <div className="m"><IcPin /><span className="v">{data.branch.name}</span></div>
                    )}
                </div>

                {/* Amber drift strip — an OVERLAY above the reachability
                    layer, not part of it: the dot/card border above keep
                    their normal presence color (green/purple/red). The box
                    is reachable and fine; only its clock is misconfigured. */}
                {showDeviceClockInfo && timezoneMismatch && (
                    <div className="pp-scr-tzwarn">
                        <IcTriangleWarn />
                        <span>
                            {t('devices.tz_drift_prefix')} <b>{driftDirection} {driftAmount}</b> {t('devices.tz_drift_suffix', { branchZone: branchZoneLabel })}
                        </span>
                    </div>
                )}

                {/* content_corrupted EXHAUSTED — same OVERLAY treatment as
                    the drift strip above: dot/card border unaffected. */}
                {deviceStatus === 'content_corrupted' && !isContentCorruptedRetrying && (
                    <div className="pp-scr-reviewstrip">
                        <IcTriangleWarn />
                        <span>
                            {t('devices.content_exhausted_review', { n: contentCorruptedAttempts })}
                            {' '}
                            <span className="pp-scr-good">{t('devices.content_no_visit_needed')}</span>
                        </span>
                    </div>
                )}

                <div className="pp-scr-cfoot">
                    {/* دفع محتوى — the primary action on every card, every
                        status, including offline (it queues for delivery
                        the moment the device reconnects; same
                        openAssignDialog/assign-campaign flow as before,
                        just promoted from the ⋮ menu to the prominent
                        honey button). Exactly one state-dependent secondary
                        action next to it now; every other action (screenshot,
                        preview, reissue-token, power toggle, mute, AI, etc.)
                        stays reachable in the card ⋮ — nothing removed. */}
                    <button type="button" className="pp-scr-push"
                        title={isOnline ? t('devices.action_push_content') : t('devices.action_push_content_offline')}
                        onClick={fetchingCampaign ? undefined : openAssignDialog}>
                        <IcPush />{t('devices.action_push_content')}
                    </button>
                    {deviceStatus === 'online' && (
                        <button type="button" className="pp-scr-act"
                            title={isPlaying ? t('buttons.pause') : t('devices.group_play')}
                            aria-label={isPlaying ? t('buttons.pause') : t('devices.group_play')}
                            onClick={() => openConfirmDialog(isPlaying ? "pause" : "play")}>
                            {isPlaying ? <IcPause /> : <IcPlay />}
                        </button>
                    )}
                    {deviceStatus === 'standby' && (
                        // Standby's Play must exit the soft-lock (turn_on, same
                        // FCM path as the maintenance menu's "تشغيل"/Start item
                        // and the app-closed launch button below), not send a
                        // plain RTDB "play" — that resumed playback directly
                        // without ever clearing presence.standby, leaving the
                        // dashboard stuck on Standby even though the device was
                        // actually playing again.
                        <button type="button" className="pp-scr-act"
                            title={t('devices.group_play')}
                            aria-label={t('devices.group_play')}
                            onClick={startAppOnDevice}>
                            <IcPlay />
                        </button>
                    )}
                    {/* Offline/scheduled_off (unreachable either way): no quick action
                        here at all — nothing on the web side can reach an unreachable
                        device, so a button here would either silently no-op or (Restart)
                        risk masking that the device needs on-site attention. The meta
                        line above covers both (on-site hint for offline, resume time for
                        scheduled_off). */}
                    {deviceStatus === 'app_closed' && !!data.fcm_token && (
                        <button type="button" className="pp-scr-act" title={t('devices.action_launch_app')} aria-label={t('devices.action_launch_app')} onClick={startAppOnDevice}>
                            <IcPlay />
                        </button>
                    )}
                    <span className="spacer"></span>
                    <button type="button" className="pp-scr-open" onClick={goToInfo}>
                        {t('devices.open_device')}<IcOpen />
                    </button>
                </div>
            </div>

            {sharedTail}
        </>
    );
};

export default React.memo(DeviceCard);
